import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import {
	differenceInMinutes,
	differenceInHours,
	differenceInDays,
	differenceInWeeks,
} from 'date-fns';

const messages = defineMessages({
	now: {
		id: 'inline-comments-common.relative-timestamp.now',
		defaultMessage: 'Now',
		description: 'Timestamp showing "Now" for very recent comments (< 2 minutes)',
	},
	minutes: {
		id: 'inline-comments-common.relative-timestamp.minutes',
		defaultMessage: '{number}m',
		description: 'Timestamp showing minutes ago (< 1 hour)',
	},
	hours: {
		id: 'inline-comments-common.relative-timestamp.hours',
		defaultMessage: '{number}h',
		description: 'Timestamp showing hours ago (< 1 day)',
	},
	days: {
		id: 'inline-comments-common.relative-timestamp.days',
		defaultMessage: '{number}d',
		description: 'Timestamp showing days ago (≤ 1 week)',
	},
});

interface RelativeTimestampProps {
	date: Date;
}

export const RelativeTimestamp: React.FC<RelativeTimestampProps> = ({ date }) => {
	const intl = useIntl();
	const now = new Date();
	const diffInMinutes = differenceInMinutes(now, date);
	const diffInHours = differenceInHours(now, date);
	const diffInDays = differenceInDays(now, date);
	const diffInWeeks = differenceInWeeks(now, date);

	// < 2 minutes: "Now"
	if (diffInMinutes < 2) {
		return <>{intl.formatMessage(messages.now)}</>;
	}

	// < 1 hour: Shows minutes (e.g., "15m")
	if (diffInMinutes < 60) {
		return <>{intl.formatMessage(messages.minutes, { number: diffInMinutes })}</>;
	}

	// < 1 day: Shows hours (e.g., "3h")
	if (diffInHours < 24) {
		return <>{intl.formatMessage(messages.hours, { number: diffInHours })}</>;
	}

	// ≤ 1 week: Shows days (e.g., "5d")
	if (diffInWeeks < 1) {
		return <>{intl.formatMessage(messages.days, { number: diffInDays })}</>;
	}

	// > 1 week: Shows actual date
	const currentYear = now.getFullYear();
	const dateYear = date.getFullYear();

	// Same year: "Jan 15"
	if (currentYear === dateYear) {
		return (
			<>
				{intl.formatDate(date, {
					month: 'short',
					day: 'numeric',
				})}
			</>
		);
	}

	// Different year: "Jan 15, 2023"
	return (
		<>
			{intl.formatDate(date, {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			})}
		</>
	);
};
